import { useMutation, useQueryClient } from '@tanstack/react-query';
import { correctMerchantReceipt } from '../service/receipts.service';
import {
  merchantReceiptDetailQueryOptions,
  merchantReceiptsQueryKeyPrefix,
} from '../service/query-options';
import type {
  MerchantReceiptCorrectInput,
  MerchantReceiptCorrectionItem,
  MerchantReceiptDetail,
} from '../service/types';

export function useCorrectMerchantReceiptMutation(transactionId: string) {
  const queryClient = useQueryClient();
  const detailQueryKey = merchantReceiptDetailQueryOptions(transactionId).queryKey;

  return useMutation({
    mutationFn: (payload: MerchantReceiptCorrectInput) =>
      correctMerchantReceipt(transactionId, payload),
    onSuccess: async (response, payload) => {
      queryClient.setQueryData<MerchantReceiptDetail | undefined>(detailQueryKey, (previous) => {
        if (!previous) {
          return previous;
        }

        return {
          ...previous,
          // Promote the receipt to a usable state, mirroring the backend:
          // a successful correction always yields status=done + needs_review=false.
          status: response.status ?? 'done',
          needs_review: false,
          store_name: payload.store_name ?? previous.store_name,
          total_centavos:
            payload.total_centavos !== undefined && payload.total_centavos !== null
              ? String(payload.total_centavos)
              : deriveTotalFromItems(payload.items) ?? previous.total_centavos,
          merchant_corrected_at: response.merchant_corrected_at,
          items: applyCorrectionItems(previous.items, payload.items),
        };
      });

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: detailQueryKey }),
        queryClient.invalidateQueries({ queryKey: merchantReceiptsQueryKeyPrefix }),
      ]);
    },
  });
}

function deriveTotalFromItems(
  nextItems: MerchantReceiptCorrectInput['items'],
): string | null {
  if (!nextItems?.length) {
    return null;
  }

  let sum = 0;
  for (const item of nextItems) {
    const value = Number(item.line_total_centavos);
    if (!Number.isFinite(value)) {
      return null;
    }
    sum += value;
  }

  return sum > 0 ? String(sum) : null;
}

function applyCorrectionItems(
  previousItems: MerchantReceiptDetail['items'],
  nextItems: MerchantReceiptCorrectInput['items'],
): MerchantReceiptDetail['items'] {
  if (!nextItems?.length) {
    return previousItems;
  }

  const correctionsByLine = new Map<number, MerchantReceiptCorrectionItem>();
  for (const item of nextItems) {
    correctionsByLine.set(item.line_no, item);
  }

  return previousItems.map((item) => {
    const correction = correctionsByLine.get(item.line_no);
    if (!correction) {
      return item;
    }

    return {
      ...item,
      name: correction.name,
      qty: String(correction.qty),
      line_total_centavos: String(correction.line_total_centavos),
    };
  });
}
